import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getCompanies, getAllCoupons, createCoupon, updateCoupon, deleteCoupon } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, Pencil, Trash2, Search, Ticket, QrCode } from 'lucide-react';
import AdminSidebar from '@/components/AdminSidebar';

const AdminCoupons = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCompany, setFilterCompany] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [formData, setFormData] = useState({
    company_id: '',
    title: '',
    description: '',
    discount: '',
    code: '',
    qr_data: '',
    barcode_image_url: '',
    expires_at: '',
    is_active: true
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }
    loadData();
  }, [isAuthenticated, navigate]);

  const loadData = async () => {
    try {
      const [companiesRes, couponsRes] = await Promise.all([
        getCompanies(),
        getAllCoupons()
      ]);
      setCompanies(companiesRes.data);
      setCoupons(couponsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company?.name || 'Empresa desconocida';
  };

  const handleOpenModal = (coupon = null) => {
    if (coupon) {
      setSelectedCoupon(coupon);
      setFormData({
        company_id: coupon.company_id || '',
        title: coupon.title || '',
        description: coupon.description || '',
        discount: coupon.discount || '',
        code: coupon.code || '',
        qr_data: coupon.qr_data || '',
        barcode_image_url: coupon.barcode_image_url || '',
        expires_at: coupon.expires_at || '',
        is_active: coupon.is_active !== false
      });
    } else {
      setSelectedCoupon(null);
      setFormData({
        company_id: filterCompany !== 'all' ? filterCompany : '',
        title: '',
        description: '',
        discount: '',
        code: '',
        qr_data: '',
        barcode_image_url: '',
        expires_at: '',
        is_active: true
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.company_id) {
      console.error('Debes seleccionar una empresa');
      return;
    }
    try {
      if (selectedCoupon) {
        await updateCoupon(selectedCoupon.id, formData);
      } else {
        await createCoupon(formData);
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      console.error('Error saving coupon:', error);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteCoupon(selectedCoupon.id);
      setShowDeleteDialog(false);
      setSelectedCoupon(null);
      loadData();
    } catch (error) {
      console.error('Error deleting coupon:', error);
    }
  };

  const filteredCoupons = coupons.filter(c => {
    const matchesSearch = c.title.toLowerCase().includes(search.toLowerCase()) ||
                         c.code?.toLowerCase().includes(search.toLowerCase());
    const matchesCompany = filterCompany === 'all' || c.company_id === filterCompany;
    return matchesSearch && matchesCompany;
  });

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-[#F8F9FA] grid grid-cols-1 md:grid-cols-[240px_1fr]">
      <AdminSidebar />
      
      <main className="p-6 md:p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="coupons-title">
                Gestión de Cupones
              </h1>
              <p className="text-[#64748B] mt-1">
                {coupons.length} cupones registrados
              </p>
            </div>
            <Button onClick={() => handleOpenModal()} className="btn-accent" data-testid="add-coupon-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Cupón
            </Button>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Buscar cupones..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-coupons"
              />
            </div>
            <Select value={filterCompany} onValueChange={setFilterCompany}>
              <SelectTrigger data-testid="filter-company">
                <SelectValue placeholder="Filtrar por empresa" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las empresas</SelectItem>
                {companies.map(company => (
                  <SelectItem key={company.id} value={company.id}>{company.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
            {loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="spinner"></div>
              </div>
            ) : filteredCoupons.length === 0 ? (
              <div className="text-center py-20">
                <Ticket className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-[#64748B]">No se encontraron cupones</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Título</TableHead>
                    <TableHead>Empresa</TableHead>
                    <TableHead>Descuento</TableHead>
                    <TableHead>Código</TableHead>
                    <TableHead className="w-24">Estado</TableHead>
                    <TableHead className="w-32">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCoupons.map((coupon) => (
                    <TableRow key={coupon.id} data-testid={`coupon-row-${coupon.id}`}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {(coupon.qr_data || coupon.barcode_image_url) && (
                            <QrCode className="w-4 h-4 text-[#d1223b]" />
                          )}
                          {coupon.title}
                        </div>
                      </TableCell>
                      <TableCell>{getCompanyName(coupon.company_id)}</TableCell>
                      <TableCell>
                        <Badge className="bg-[#d1223b]/10 text-[#d1223b]">
                          {coupon.discount || '-'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {coupon.code ? (
                          <code className="bg-slate-100 px-2 py-1 rounded text-sm">{coupon.code}</code>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={coupon.is_active ? 'default' : 'secondary'} className={coupon.is_active ? 'bg-emerald-100 text-emerald-700' : ''}>
                          {coupon.is_active ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenModal(coupon)}
                            data-testid={`edit-coupon-${coupon.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => {
                              setSelectedCoupon(coupon);
                              setShowDeleteDialog(true);
                            }}
                            data-testid={`delete-coupon-${coupon.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      </main>

      {/* Coupon Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
              {selectedCoupon ? 'Editar Cupón' : 'Nuevo Cupón'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="company_id">Empresa *</Label>
              <Select 
                value={formData.company_id} 
                onValueChange={(value) => setFormData({...formData, company_id: value})}
                disabled={!!selectedCoupon}
              >
                <SelectTrigger data-testid="coupon-company-select">
                  <SelectValue placeholder="Seleccionar empresa" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map(company => (
                    <SelectItem key={company.id} value={company.id}>{company.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Título *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Ej: 10% de descuento"
                  required
                  data-testid="coupon-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="discount">Descuento</Label>
                <Input
                  id="discount"
                  value={formData.discount}
                  onChange={(e) => setFormData({...formData, discount: e.target.value})}
                  placeholder="Ej: 10%, 2x1, Gratis"
                  data-testid="coupon-discount-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descripción</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={2}
                placeholder="Descripción del cupón y condiciones"
                data-testid="coupon-description-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="code">Código del Cupón</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                  placeholder="Ej: CLUB2024"
                  data-testid="coupon-code-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="expires_at">Fecha de Expiración</Label>
                <Input
                  id="expires_at"
                  type="date"
                  value={formData.expires_at}
                  onChange={(e) => setFormData({...formData, expires_at: e.target.value})}
                  data-testid="coupon-expires-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="qr_data">Datos QR / URL para código QR</Label>
              <Input
                id="qr_data"
                value={formData.qr_data}
                onChange={(e) => setFormData({...formData, qr_data: e.target.value})}
                placeholder="URL o texto para generar el código QR"
                data-testid="coupon-qr-input"
              />
              <p className="text-xs text-[#64748B]">Este texto se codificará en el código QR mostrado al cliente</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="barcode_image_url">URL de Imagen de Código de Barras</Label>
              <Input
                id="barcode_image_url"
                value={formData.barcode_image_url}
                onChange={(e) => setFormData({...formData, barcode_image_url: e.target.value})}
                placeholder="https://... (opcional)"
                data-testid="coupon-barcode-input"
              />
              <p className="text-xs text-[#64748B]">Si la empresa proporciona una imagen de código de barras</p>
            </div>

            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <Label htmlFor="is_active" className="text-base font-medium">Cupón Activo</Label>
                <p className="text-sm text-[#64748B]">Los cupones inactivos no se muestran a los clientes</p>
              </div>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                data-testid="coupon-active-switch"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="btn-accent" data-testid="save-coupon-btn">
                {selectedCoupon ? 'Guardar Cambios' : 'Crear Cupón'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar cupón?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará el cupón "{selectedCoupon?.title}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600" data-testid="confirm-delete-coupon-btn">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminCoupons;
